import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { PermissionsService } from '@services/auth/permissions.service';
import { DatasetsService } from '@services/datasets/datasets.service';
import { ResultsService } from '@services/results/results.service';
import { SimulationSetService } from '@services/simulation-set/simulation-set.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';

@Component({
  selector: 'app-simulation-set',
  templateUrl: './simulation-set.component.html',
  styleUrls: ['./simulation-set.component.scss'],
})
export class SimulationSetComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('newBaselineSelector', { static: false }) newbaseSelect: ElementRef;
  private destroy$ = new Subject();

  public get simulationSetState$() {
    return this.simService.simulationSetState$;
  }

  public get fetchingData() {
    return this.simService.DBstate === 'fetching' || this.simService.buildingSimulationSetState;
  }

  public get datasets$() {
    return this.simService.datasetArray$;
  }

  public get baselines$() {
    return this.simService.baselineList$;
  }

  public get stresses$() {
    return this.simService.stressList$;
  }

  public get reorderingLocked(): string {
    var response = '';
    const simulationSet = this.simulationSetState$.getValue();
    if (!this.simService.canBeReordered('root', simulationSet)) {
      response = 'Baseline reordering DISABLED.';
    }
    if (simulationSet) {
      simulationSet.find(baseline => {
        if (!this.simService.canBeReordered(baseline.RunID, baseline.StressList)) {
          response += 'Stress reordering DISABLED.';
          return true;
        }
        return false;
      });
    }
    return response === '' ? null : 'To reorder resultsets all previous active resultset must be present. ' + response;
  }

  private identifiersSub: Subscription;
  private userRoleHasAccessToImport;
  private isRequiredImport;

  constructor(
    private simService: SimulationSetService,
    private renderer: Renderer2,
    private resultsService: ResultsService,
    private permissionService: PermissionsService,
    private dataSetService: DatasetsService
  ) {}

  ngOnInit() {
    this.simService.loadDataFromDB();

    // Force load resultsetidentifiers in the cache
    this.identifiersSub = this.resultsService.getResultSetIdentifiers().pipe(takeUntil(this.destroy$)).subscribe();

    this.userRoleHasAccessToImport = this.permissionService.getPermission('Import') === 'W';
  }

  ngAfterViewInit() {
    if (!this.userRoleHasAccessToImport) {
      this.dataSetService.getRequiredDatasetImport().then(_ => {
        this.isRequiredImport = this.dataSetService.isImportRequired;
        if (this.isRequiredImport) {
          this.showImportRequiredDialog();
        }
      });
    }
  }

  ngOnDestroy(): void {
    // Clear cache of resultset identifiers on destroy
    this.resultsService.clearResultSetIdentifiers();
    this.identifiersSub.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleAddNewBaseline = event => {
    const scenarioIdForBaseline = Number(event.target.value);
    this.simService.addNewBaselineToState(scenarioIdForBaseline);
    this.renderer.setProperty(this.newbaseSelect.nativeElement, 'selected', 'true');
  };

  private showImportRequiredDialog() {
    return Swal.fire({
      title: 'Import Required',
      text: `You cannot run a simulation because an import is required or it is in progress.`,
      type: 'warning',
      confirmButtonText: 'Dismiss',
      allowEscapeKey: true,
    } as SweetAlertOptions);
  }
}
